import React from "react";
import styled from "styled-components";
import Box from "react-raster";
import { Copy, H3 } from "../Typography";
import Container from "../Container";
import ButtonLink from "../ButtonLink";
import { getImage, GatsbyImage } from "gatsby-plugin-image";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  background-color: ${({ theme, $backgroundColour }) =>
    theme.colours[$backgroundColour]};
  padding-bottom: ${({ $hasBottomMargin }) =>
    ($hasBottomMargin && "60px") || "20px"};
  padding-top: ${({ $hasTopMargin }) => ($hasTopMargin && "60px") || "20px"};
  @media (max-width: ${({ theme }) => `${theme.breakpoints[1]}px`}) {
    flex-wrap: wrap;
    row-gap: 30px;
  }
`;

const LogosWrapper = styled.div`
  display: flex;
  width: 100%;
`;

const Logo = styled.div`
  display: grid;
  grid-template-columns: minmax(90px, 1fr);
  grid-gap: 5px;
`;

const ImageColumn = styled(Box)`
  width: 100%;
  order: ${({ $imagePosition }) => ($imagePosition === "left" ? 1 : 2)};
  @media (max-width: ${({ theme }) => `${theme.breakpoints[1]}px`}) {
    order: 1;
  }
`;

const ContentColumn = styled(Box)`
  order: ${({ $imagePosition }) => ($imagePosition === "left" ? 2 : 1)};
  @media (max-width: ${({ theme }) => `${theme.breakpoints[1]}px`}) {
    order: 2;
  }
`;

const HeadlineLink = styled.a`
  color: ${({ theme, $colour }) => theme.colours[$colour]};
  text-decoration: none;
`;

function Content({ title, description, backgroundColour = "black", ctaUrl }) {
  return (
    <>
      <Box cols={[6, 8, 6]} marginBottom="22px">    
        <H3>
          <HeadlineLink
              $colour={
                backgroundColour === "white" ||
                backgroundColour === "alabasterWhite"
                  ? "black"
                  : "white"
              }
            href={ctaUrl || "#"}
          >
            {title}
          </HeadlineLink>
        </H3>
      </Box>
      <Box cols={[6, 8, 5]} marginBottom="22px">
        <Copy
          colour={
            backgroundColour === "white" ||
            backgroundColour === "alabasterWhite"
              ? "black"
              : "white"
          }
        >
          {description}
        </Copy>
      </Box>
    </>
  );
}

function Logos({ logos }) {
  return (
    <Box cols={[6, 6, 6]} marginBottom="22px">
      <LogosWrapper>
        {logos?.map((logoObj) => {
          const { id, description } = logoObj;
          return (
            <Logo key={id}>
              <GatsbyImage image={getImage(logoObj)} alt={description} />
            </Logo>
          );
        })}
      </LogosWrapper>
    </Box>
  );
}

function CtaLink({ ctaOpensChatWindow, ctaLabel, ctaUrl, backgroundColour }) {
  return (
    <Box cols={[6, 6, 6]} marginTop="22px">
      <ButtonLink
        url={ctaUrl || "#"}
        label={ctaLabel}
        isWhite={
          backgroundColour === "white" || backgroundColour === "alabasterWhite"
        }
        onClick={(event) => {
          if (ctaOpensChatWindow) {
            event.preventDefault();
            window.LeadBooster?.trigger("open");
          }
        }}
      />
    </Box>
  );
}

function ImpactStorySummary(props) {
  const {
    backgroundColour,
    ctaOpensChatWindow,
    hasBottomMargin,
    hasTopMargin,
    ctaUrl,
    image,
    imagePosition,
    logos,
  } = props;

  return image ? (
    <Wrapper
      $backgroundColour={backgroundColour}
      $hasBottomMargin={hasBottomMargin}
      $hasTopMargin={hasTopMargin}
    >
      <ImageColumn
        cols={[6, 6, 6]}
        marginLeft={[0, 0, 0]}
        $imagePosition={imagePosition}
        paddingBottom="22px"
      >
        <a href={ctaUrl || "#"}>
          <GatsbyImage image={getImage(image)} alt={props.title || "logo"} />
        </a>
      </ImageColumn>
      <ContentColumn
        cols={[6, 6, 6]}
        paddingBottom="22px"
        width="100%"
        marginLeft={[0, 0, 0.5]}
        marginRight={[0, 0, 0.5]}
        $imagePosition={imagePosition}
      >
        <Content {...props} />
        {logos && <Logos {...props} />}
        {ctaUrl || ctaOpensChatWindow ? <CtaLink {...props} /> : null}
      </ContentColumn>
    </Wrapper>
  ) : (
    <Wrapper
      $backgroundColour={backgroundColour}
      $hasBottomMargin={hasBottomMargin}
      $hasTopMargin={hasTopMargin}
    >
      <Container>
        <ContentColumn
          cols={[6, 8, 10]}
          width="100%"
          marginLeft={[0, 0.5, 0.5]}
          marginRight={[0, 0.5, 0.5]}
        >
          <Content {...props} />
          {logos && <Logos {...props} />}
          {ctaUrl || ctaOpensChatWindow ? <CtaLink {...props} /> : null}
        </ContentColumn>
      </Container>
    </Wrapper>
  );
}

export default ImpactStorySummary;
